import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon, UserPlusIcon, PlusCircleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const EMPTY_ROW = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  firstName: '',
  lastName: '',
  email: '',
  mobile: '+91',
});

/**
 * Standalone invite modal for WhatsApp Agent screenings. Deliberately NOT the shared
 * Email/Single/Bulk "Invite Candidates" modal — a WhatsApp Agent screening only ever
 * invites over WhatsApp, and submitting here starts the actual conversational interview
 * (via POST /whatsapp-agent-invite), not a link-based email/SMS invite.
 */
export default function WhatsAppInviteModal({ screeningId, onClose, onInvited }) {
  const [rows, setRows] = useState([EMPTY_ROW()]);
  const [submitting, setSubmitting] = useState(false);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleMobileChange = (id, value) => {
    // Keep it simple: strip non-digits/plus, same +91 default convention as the rest of the app.
    const digits = value.replace(/[^\d]/g, '');
    updateRow(id, { mobile: digits ? `+91${digits.replace(/^91/, '')}` : '+91' });
  };

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const removeRow = (id) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));

  const canSubmit = rows.some((r) => r.mobile && r.mobile !== '+91' && r.mobile.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    const candidates = rows
      .map((r) => ({
        name: [r.firstName.trim(), r.lastName.trim()].filter(Boolean).join(' ').trim(),
        email: r.email.trim() || undefined,
        mobile: r.mobile === '+91' ? '' : r.mobile.trim(),
      }))
      .filter((r) => r.mobile);
    if (!candidates.length) return;

    setSubmitting(true);
    try {
      const res = await axios.post(`/api/screenings/${screeningId}/whatsapp-agent-invite`, { candidates });
      const { summary } = res.data || {};
      if (summary?.started > 0) {
        toast.success(`Started ${summary.started} WhatsApp interview${summary.started === 1 ? '' : 's'}`);
      }
      if (summary?.failed > 0) {
        toast.error(`${summary.failed} invite${summary.failed === 1 ? '' : 's'} failed to send`);
      }
      onInvited?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start WhatsApp interviews');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-3xl max-h-[85vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div style={{ height: 3, background: 'linear-gradient(90deg, #25D366, #22C55E)' }} />
        <header className="flex items-center justify-between shrink-0 border-b border-gray-100 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#25D366]">
              <ChatBubbleLeftRightIcon className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-900">Invite via WhatsApp</h2>
              <p className="text-xs mt-0.5 text-slate-500">Add candidates to interview over WhatsApp</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <form id="whatsapp-invite-form" onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="shrink-0 border-b border-slate-100 px-6 py-3 bg-[#25D366]/5">
            <div className="grid grid-cols-[1fr,1fr,1.2fr,1.2fr,0.6fr] items-center text-xs font-bold text-slate-700">
              <span>First name</span>
              <span>Last name</span>
              <span>Email</span>
              <span>Mobile</span>
              <span className="sr-only">Actions</span>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
            {rows.map((row, index) => (
              <div key={row.id} className="grid grid-cols-[1fr,1fr,1.2fr,1.2fr,0.6fr] items-center gap-3">
                <input
                  type="text"
                  value={row.firstName}
                  onChange={(e) => updateRow(row.id, { firstName: e.target.value })}
                  placeholder="First name"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-[#25D366] focus:outline-none focus:ring-2 focus:ring-[#25D366]/20"
                />
                <input
                  type="text"
                  value={row.lastName}
                  onChange={(e) => updateRow(row.id, { lastName: e.target.value })}
                  placeholder="Last name"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-[#25D366] focus:outline-none focus:ring-2 focus:ring-[#25D366]/20"
                />
                <input
                  type="email"
                  value={row.email}
                  onChange={(e) => updateRow(row.id, { email: e.target.value })}
                  placeholder="Email (Optional)"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-[#25D366] focus:outline-none focus:ring-2 focus:ring-[#25D366]/20"
                />
                <input
                  type="text"
                  required={index === 0}
                  value={row.mobile}
                  onChange={(e) => handleMobileChange(row.id, e.target.value)}
                  placeholder="Mobile *"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-[#25D366] focus:outline-none focus:ring-2 focus:ring-[#25D366]/20"
                />
                {index === 0 ? (
                  <button
                    type="button"
                    onClick={addRow}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-[#1c9950] hover:border-[#25D366]/40 hover:bg-[#25D366]/5"
                  >
                    <PlusCircleIcon className="h-4 w-4" />
                    Add
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-medium text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </form>

        <footer className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            type="submit"
            form="whatsapp-invite-form"
            disabled={!canSubmit || submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#22C55E] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <UserPlusIcon className="h-4 w-4" />
            <span>{submitting ? 'Starting…' : 'Start WhatsApp Interview'}</span>
          </button>
        </footer>
      </div>
    </div>
  );
}
